import gql from 'graphql-tag';
import {flowRight as compose} from 'lodash';
import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import {Mutation, useQuery, withApollo} from 'react-apollo';
import {Col, Container, Row} from 'react-bootstrap';
import {withTranslation} from 'react-i18next';
import {withRouter} from 'react-router';
import {PulseLoader} from 'react-spinners';
import styled from 'styled-components';
import {checkErrorType, dateFormatFR} from '../../../../utils';
import {buildFormToSubmit, formStateWithData, initFormState, validateFormState} from '../../../../utils/form';
import FormInputFactory from '../../../../common/components/layout/FormInputFactory';
import InputCol, {AttributeDiv} from '../../../../common/components/layout/InputCol';
import SelectType from '../../../../common/components/Merchant/SelectType';
import {CancelButton, HeadDiv, PermanentDeleteButton, TitleButton} from '../../../../common/components/StyledComp';
import {ERROR_TYPE_EMAIL_ALREADY_USED} from '../../../../common/constants';
import DisplayBrandWithSelect from '../../../brand/components/DisplayBrandWithSelect';
import ConfirmModal from '../../../../common/components/ConfirmModal';
import Error from '../../../../common/components/Error/Error';

const Div = styled.div`
  text-transform: uppercase;
  font-family: 'Segoe UI Bold', sans-serif;
  font-size: 12px;
`;

const UnitDiv = styled.div`
  width: 10%;
  background: #eff0f4;
  height: 36px;
  font-weight: bold;
  color: #565484;
  border: 0.5px solid #d0d1df;
  border-top-right-radius: 5px;
  border-bottom-right-radius: 5px;
  vertical-align: middle;
  display: flex;
  align-items: center;
  justify-content: center;
  border-left: 0;
`;

const FlexDiv = styled.div`
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 90%;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
`;

const CancelButtonForm = styled(CancelButton)`
  margin-top: 0;
  height: auto;
  width: auto;
`;

const GRAPHQL_QUERY_MERCHANT = gql`
  query merchant($id: ID!) {
    merchant(id: $id) {
      id
      brand {
        id
        name
      }
      user {
        id
        firstName
        lastName
        email
        postalCode
        street
        city
        isDeleted
      }
      contactEmail
      createdAt
      businessName
      siret
      salesArea
      type
      intraVatNumber
      phoneNumber
      totalAvailableNotification
      totalReceipts
    }
  }
`;


export const GRAPHQL_MUTATION_UPDATE_MERCHANT = gql`
  mutation updateMerchant($userInput: UpdateUserInput!, $merchantInput: UpdateMerchantInput!) {
    updateMerchant(userInput: $userInput, merchantInput: $merchantInput) {
      id
      firstName
      lastName
      isActive
      isDeleted
      email
      postalCode
      street
      city
      merchant{
        totalAvailableNotification
        id
        businessName
        contactEmail
        phoneNumber
        siret
        salesArea
        type
        intraVatNumber
        createdAt
        updatedAt
        totalAvailableNotification
        brand {
          id
          name
        }
      }
    }
  }
`;

const GRAPHQL_MUTATION_DELETE_MERCHANT = gql`
  mutation deleteMerchant($merchantId: ID!){
    deleteMerchant(merchantId: $merchantId)
  }
`;

const formFactory = {
  userId: {
    initial: 0,
    fromData: 'merchant.user.id',
    toSubmit: 'userInput.id',
    required: true,
    type: 'number',
  },
  firstName: {
    initial: '',
    fromData: 'merchant.user.firstName',
    toSubmit: 'userInput.firstName',
    required: true,
    type: 'text',
  },
  lastName: {
    initial: '',
    fromData: 'merchant.user.lastName',
    toSubmit: 'userInput.lastName',
    required: true,
    type: 'text',
  },
  email: {
    initial: '',
    fromData: 'merchant.user.email',
    toSubmit: 'userInput.email',
    required: true,
    type: 'email',
  },
  brandId: {
    initial: 0,
    fromData: 'merchant.brand.id',
    toSubmit: 'merchantInput.brandId',
    required: true,
    type: 'number',
  },
  businessName: {
    initial: '',
    fromData: 'merchant.businessName',
    toSubmit: 'merchantInput.businessName',
    required: true,
    type: 'text',
  },
  street: {
    initial: '',
    fromData: 'merchant.user.street',
    toSubmit: 'userInput.street',
    required: true,
    type: 'text',
  },
  postalCode: {
    initial: '',
    fromData: 'merchant.user.postalCode',
    toSubmit: 'userInput.postalCode',
    required: true,
    type: 'text',
  },
  city: {
    initial: '',
    fromData: 'merchant.user.city',
    toSubmit: 'userInput.city',
    required: true,
    type: 'text',
  },
  contactEmail: {
    initial: '',
    fromData: 'merchant.contactEmail',
    toSubmit: 'merchantInput.contactEmail',
    required: true,
    type: 'email',
  },
  phoneNumber: {
    initial: '',
    fromData: 'merchant.phoneNumber',
    toSubmit: 'merchantInput.phoneNumber',
    required: true,
    type: 'text',
  },
  siret: {
    initial: '',
    fromData: 'merchant.siret',
    toSubmit: 'merchantInput.siret',
    required: true,
    type: 'text',
  },
  salesArea: {
    initial: 0,
    fromData: 'merchant.salesArea',
    toSubmit: 'merchantInput.salesArea',
    required: false,
    type: 'number',
  },
  merchantType: {
    initial: '',
    fromData: 'merchant.type',
    toSubmit: 'merchantInput.type',
    required: false,
    type: 'text',
  },
  intraVatNumber: {
    initial: 0,
    fromData: 'merchant.intraVatNumber',
    toSubmit: 'merchantInput.intraVatNumber',
    required: false,
    type: 'text',
  },
  createdAt: {
    initial: '',
    fromData: 'merchant.createdAt',
    toSubmit: false,
    required: false,
    type: 'text',
  },
  totalReceipts: {
    initial: 0,
    fromData: 'merchant.totalReceipts',
    toSubmit: false,
  },
  isDeleted: {
    initial: false,
    fromData: 'merchant.user.isDeleted',
    toSubmit: false,
  },
};

const initialState = initFormState(formFactory);

const MerchantDetails = ({merchantId, t, history}) => {
  const [merchantValues, setMerchantValues] = useState(initialState);

  const onSelectChange = e => {
    setMerchantValues({
      ...merchantValues,
      [e.target.name]: formFactory[e.target.name].type === 'number' ? Number(e.target.value) : e.target.value,
    });
  };

  const setState = (name, value) => {
    setMerchantValues({
      ...merchantValues,
      [name]: formFactory[name].type === 'number' ? +value : value,
    });
  };

  const {loading, error, data} = useQuery(GRAPHQL_QUERY_MERCHANT, {
    variables: {id: merchantId},
  });

  useEffect(() => {
    if (data && !loading) {
      const newStateFilled = formStateWithData(data, formFactory);
      setMerchantValues(newStateFilled);
    }
  }, [data, loading]);

  const submitChanges = (e, updateMerchant) => {
    e.preventDefault();
    if (validateFormState(formFactory, merchantValues)) {

      const variables = buildFormToSubmit(formFactory, merchantValues);
      updateMerchant({
        variables,
      })
        .catch(error => {
          console.error(t('failed') + error);
        });
    }
  };
  if (loading) {
    return <PulseLoader/>;
  } else if (error || !data.merchant) {
    return <h4>{t('noMerchant')}</h4>;
  } else {
    return (
      <Mutation
        mutation={GRAPHQL_MUTATION_UPDATE_MERCHANT}
        variables={merchantValues}
        onCompleted={() => history.push('/admin/merchants')}
      >
        {(updateMerchant, {error, loading: mutationLoading}) => {
          const existingEmail = checkErrorType(error, ERROR_TYPE_EMAIL_ALREADY_USED);
          if (error && !existingEmail) {
            console.error(error);
          }
          return (
            <>
              <form>
                <Container className="general-container">
                  <Row className="info-row">
                    <Col xs={6}>
                      <Row className="section-info">
                        <HeadDiv className="info-header">{`${t('personalInfos')} : `}</HeadDiv>
                        <FormInputFactory
                          nameInput="lastName"
                          formFactory={formFactory}
                          formState={merchantValues}
                          onChange={setState}
                        />
                        <FormInputFactory
                          nameInput="firstName"
                          formFactory={formFactory}
                          formState={merchantValues}
                          onChange={setState}
                        />
                        <FormInputFactory
                          nameInput="email"
                          formFactory={formFactory}
                          formState={merchantValues}
                          onChange={setState}
                          translationValue="personalEmail"
                          customText={existingEmail ? t('existingEmail') : t('wrongMail')}
                        />
                      </Row>
                    </Col>
                    <Col xs={6}>
                      <DisplayBrandWithSelect
                        onChange={setState}
                        values={merchantValues}
                      />
                    </Col>
                  </Row>
                  <Row className="info-row">
                    <Col xs={12}>
                      <Row className="section-info">
                        <HeadDiv className="info-header">{`${t('merchantInfo')} : `}</HeadDiv>
                        <FormInputFactory
                          nameInput="businessName"
                          formFactory={formFactory}
                          formState={merchantValues}
                          onChange={setState}
                          size={6}
                        />
                        <FormInputFactory
                          nameInput="siret"
                          formFactory={formFactory}
                          formState={merchantValues}
                          onChange={setState}
                          size={6}
                        />
                        <FormInputFactory
                          nameInput="street"
                          formFactory={formFactory}
                          formState={merchantValues}
                          onChange={setState}
                          size={6}
                        />
                        <FormInputFactory
                          nameInput="intraVatNumber"
                          formFactory={formFactory}
                          formState={merchantValues}
                          onChange={setState}
                          size={6}
                        />
                        <Col xs={6}>
                          <Row>
                            <FormInputFactory
                              nameInput="postalCode"
                              formFactory={formFactory}
                              formState={merchantValues}
                              onChange={setState}
                              size={3}
                            />
                            <FormInputFactory
                              nameInput="city"
                              formFactory={formFactory}
                              formState={merchantValues}
                              onChange={setState}
                              size={9}
                            />
                          </Row>
                        </Col>
                        <Col xs={6}>
                          <label>
                            <Div className="attribute-name">{`${t('type')} : `}</Div>
                            <SelectType
                              onChange={onSelectChange}
                              value={merchantValues.merchantType}
                              empty={merchantValues.merchantType === ''}
                            />
                          </label>
                        </Col>
                        <FormInputFactory
                          nameInput='contactEmail'
                          formFactory={formFactory}
                          formState={merchantValues}
                          onChange={setState}
                          customText={t('wrongMail')}
                          size={6}
                        />
                        <Col xs={6}>
                          <label>
                            <AttributeDiv>{`${t('surface')} : `}</AttributeDiv>
                            <FlexDiv>
                              <Input
                                type="number"
                                value={merchantValues.salesArea}
                                name="salesArea"
                                onChange={onSelectChange}
                              />
                              <UnitDiv>M²</UnitDiv>
                            </FlexDiv>
                          </label>
                        </Col>
                        <FormInputFactory
                          nameInput="phoneNumber"
                          formFactory={formFactory}
                          formState={merchantValues}
                          onChange={setState}
                          size={6}
                        />
                        <InputCol
                          title={`${t('creation')} : `}
                          size={6}
                          type="number"
                          name="createdAt"
                          onChange={setState}
                          disabled
                          placeholder={dateFormatFR({date:merchantValues.createdAt})}
                        />
                      </Row>
                    </Col>
                  </Row>
                </Container>
                <div className="popin-buttons">
                  <div>
                    <TitleButton
                      onClick={e => submitChanges(e, updateMerchant)}
                      disabled={mutationLoading}
                    >
                      {t('save')}
                    </TitleButton>
                  </div>
                  <CancelButtonForm onClick={() => history.push('/admin/merchants')}>{t('cancel')}</CancelButtonForm>
                  {merchantValues.isDeleted && merchantValues.totalReceipts === 0 && (
                    <Mutation
                      mutation={GRAPHQL_MUTATION_DELETE_MERCHANT}
                      refetchQueries={['merchants', 'merchantsSelect']}
                      variables={{merchantId}}
                      onCompleted={() => history.push('/admin/merchants')}
                      awaitRefetchQueries={true}
                    >
                      {deleteMerchant => {
                        return (
                          <ConfirmModal
                            title={t('permanentDelete')}
                            mutation={deleteMerchant}
                            displayError={() => <Error error={t('errorDeletingMerchant')} />}
                            size={'m'}
                            confirmBtn={'yes'}
                            cancelBtn={'no'}
                          >
                            {askConfirmation => (
                              <PermanentDeleteButton
                                onClick={askConfirmation}>{t('permanentDelete')}
                              </PermanentDeleteButton>
                            )}
                          </ConfirmModal>
                        );
                      }}
                    </Mutation>
                  )}
                </div>
              </form>
            </>
          );
        }}
      </Mutation>
    );
  }
};

MerchantDetails.propTypes = {
  t: PropTypes.func.isRequired,
  client: PropTypes.object.isRequired,
  history: PropTypes.any.isRequired,
  merchantId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default compose(
  withTranslation('merchant'),
  withRouter,
  withApollo,
)(MerchantDetails);
